import React from 'react';
import { Input } from 'reactstrap';
import {
    TermForm,
    LeaseTerms,
} from '../../../../../../slices/marketplace/loi-form/term-form';
import {
    getOptionsForAnswerType,
    LoiFormAnswerType,
} from '../../../../../../slices/marketplace/loi-form/loi-form-answer-type.enum';
import { TermsFormSelect } from './TermsFormSelect';
import './TermsFormLeaseTermsBlock.scss';

const validatePercentageField = (
    event: React.KeyboardEvent<HTMLInputElement>,
    currentValue: string
) => {
    const decimalLength = (value: string) =>
        value.includes('.') ? value.split('.').pop()?.length ?? 0 : 0;
    const selectionStart = event.currentTarget.selectionStart ?? 0;
    const isChangeAfterDot = selectionStart > currentValue.length - 2;
    const isThreeDigitsAfterDot = decimalLength(currentValue) >= 3;

    if (
        !/[0-9]/.test(event.key) &&
        event.key !== 'Backspace' &&
        event.key !== 'ArrowRight' &&
        event.key !== 'ArrowLeft' &&
        event.key !== 'Tab' &&
        (event.key !== '.' || currentValue.includes('.'))
    ) {
        return true;
    }

    if (event.key === '.' && !isChangeAfterDot) {
        return true;
    }

    if (/[0-9]/.test(event.key) && isThreeDigitsAfterDot && isChangeAfterDot) {
        return true;
    }

    return false;
};

export interface TermsFormLeaseTermsBlockProps {
    termForm: TermForm;
    readOnly?: boolean;
    onChange?: (form: TermForm) => void;
    errors?: Set<string>;
}

export const TermsFormLeaseTermsBlock: React.FC<TermsFormLeaseTermsBlockProps> = ({
    termForm,
    readOnly = false,
    onChange,
    errors,
}) => {
    const leaseTerms: LeaseTerms | null | undefined = termForm.leaseTerms;

    const update = (patch: Partial<LeaseTerms>) => {
        onChange?.({
            ...termForm,
            leaseTerms: {
                ...(leaseTerms ?? {}),
                ...patch,
            },
        });
    };

    return (
        <div className="terms-form-detail-block terms-form-section-underlined terms-form-lease-terms-block">
            <div className="terms-form-block-name">Lease Terms</div>
            <div className="terms-form-form-content">
                <div className="terms-form-lease-terms-row">
                    <div className="terms-form-lease-terms-field">
                        <span className="terms-form-lease-terms-label">Term of Lease</span>
                        <div className="terms-form-lease-terms-value">
                            <Input
                                type="text"
                                value={leaseTerms?.termOfLease ?? ''}
                                disabled={readOnly}
                                onChange={(e) => update({ termOfLease: e.target.value || null })}
                                className="form-control loi-form-input"
                                aria-label="Term of Lease"
                                invalid={errors?.has('termOfLease')}
                            />
                        </div>
                    </div>
                    <div className="terms-form-lease-terms-field">
                        <span className="terms-form-lease-terms-label">Lease Type</span>
                        <div className="terms-form-lease-terms-value">
                            <Input
                                type="text"
                                value={leaseTerms?.leaseType ?? ''}
                                disabled={readOnly}
                                onChange={(e) => update({ leaseType: e.target.value || null })}
                                className="form-control loi-form-input"
                                aria-label="Lease Type"
                                invalid={errors?.has('leaseType')}
                            />
                        </div>
                    </div>
                    <div className="terms-form-lease-terms-field">
                        <span className="terms-form-lease-terms-label">Lease Rate</span>
                        <div className="terms-form-lease-terms-value">
                            <Input
                                type="text"
                                value={
                                    leaseTerms?.leaseRate != null && leaseTerms.leaseRate !== ''
                                        ? `${leaseTerms.leaseRate}%`
                                        : ''
                                }
                                disabled={readOnly}
                                onKeyDown={(e) => {
                                    if (
                                        validatePercentageField(
                                            e,
                                            (leaseTerms?.leaseRate ?? '').toString()
                                        )
                                    ) {
                                        e.preventDefault();
                                    }
                                }}
                                onChange={(e) => {
                                    const value = e.target.value.replace('%', '');
                                    update({
                                        leaseRate: value === '' ? null : value,
                                    });
                                }}
                                className="form-control loi-form-input"
                                aria-label="Lease Rate"
                                invalid={errors?.has('leaseRate')}
                            />
                        </div>
                    </div>
                    <div className="terms-form-lease-terms-field">
                        <span className="terms-form-lease-terms-label">Lease Rate Escalation</span>
                        <div className="terms-form-lease-terms-value">
                            <Input
                                type="text"
                                value={
                                    leaseTerms?.leaseRateEscalation != null &&
                                    leaseTerms.leaseRateEscalation !== ''
                                        ? `${leaseTerms.leaseRateEscalation}%`
                                        : ''
                                }
                                disabled={readOnly}
                                onKeyDown={(e) => {
                                    if (
                                        validatePercentageField(
                                            e,
                                            (leaseTerms?.leaseRateEscalation ?? '').toString()
                                        )
                                    ) {
                                        e.preventDefault();
                                    }
                                }}
                                onChange={(e) => {
                                    const value = e.target.value.replace('%', '');
                                    update({
                                        leaseRateEscalation: value === '' ? null : value,
                                    });
                                }}
                                className="form-control loi-form-input"
                                aria-label="Lease Rate Escalation"
                                invalid={errors?.has('leaseRateEscalation')}
                            />
                        </div>
                    </div>
                    <div className="terms-form-lease-terms-field">
                        <span className="terms-form-lease-terms-label">Lease Rate Upon Extension</span>
                        <div className="terms-form-lease-terms-value">
                            <TermsFormSelect
                                value={leaseTerms?.leaseRateUponExtension ?? ''}
                                options={getOptionsForAnswerType(LoiFormAnswerType.LeaseRateUponExtension).map(
                                    (o) => o.value
                                )}
                                disabled={readOnly}
                                onChange={(e) => update({ leaseRateUponExtension: e })}
                                aria-label="Lease Rate Upon Extension"
                                invalid={errors?.has('leaseRateUponExtension')}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
